import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  AnalyticsResponse,
  DynamicQueryService,
  mapAnalyticsResponse,
  toISOString,
} from '@services/analytics-services/core';
import { ShippingAnalyticsQuery, ShippingStats } from '@services/analytics-services/shipping';
import { CachingApiService } from '@services/core-services/api-cache.service';
import { ProtectionService } from '@services/core-services/protection.service';

@Injectable({ providedIn: 'root' })
export class ShippingAnalyticsService {
  constructor(
    private readonly cacheApi: CachingApiService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly protectionService: ProtectionService
  ) {}

  getAnalytics(context: ShippingAnalyticsQuery): Observable<AnalyticsInfo<ShippingStats>> {
    const { range, interval, compare, ...params } = context;

    return this.cacheApi
      .getWithCache<AnalyticsResponse<ShippingStats>>('shipping/metrics/stats', {
        ...params,
        date_from: toISOString(compare?.start || range.start),
        date_to: toISOString(range.end),
        interval,
      })
      .pipe(
        map(response => {
          return mapAnalyticsResponse<ShippingStats>(response, context);
        })
      );
  }

  getShippingSLAStats(range: AnalyticsRangeContext, timezone?: string) {
    const storeId = this.protectionService.getCurrentStoreSnapshot()?.id || 'default_store_id';
    const userTimezone = timezone || this.dynamicQueryService.getUserTimezone();
    const startTime = this.dynamicQueryService.toLocalTimezone(range.range.start, userTimezone);
    const endTime = this.dynamicQueryService.toLocalTimezone(range.range.end, userTimezone);

    const sql = `
        select 
          slo_status, 
          count(id) as shipment_count
        from 
          ce_replica.\`ce_sales.sales_order_shipment\`
        where
            store_id = '${storeId}'
            AND status = 'delivered'
            AND slo_status != ''
            AND toTimeZone(delivered_date, '${userTimezone}') BETWEEN ${startTime} AND ${endTime}
        group by 1
    `;

    return this.dynamicQueryService.fetchRaw(range, {
      raw_query: sql,
    });
  }

  getShippingByCourier(range: AnalyticsRangeContext, timezone?: string) {
    const userTimezone = timezone || this.dynamicQueryService.getUserTimezone();
    const startTime = this.dynamicQueryService.toLocalTimezone(range.range.start, userTimezone);
    const endTime = this.dynamicQueryService.toLocalTimezone(range.range.end, userTimezone);
    const storeId = this.protectionService.getCurrentStoreSnapshot()?.id || 'default_store_id';
    const sql = `
        select 
          TRIM(courier_company_name)                                           as courier,
          uniqExact(order_id)                                                  as orders_shipped,
          ROUND(avg(date_diff('{{interval}}', created_at, delivered_date)), 1) as avg_delivery_time
        from 
          ce_replica.\`ce_sales.sales_order_shipment\`
        where 
          status = 'delivered' 
          AND store_id = '${storeId}'
          AND toTimeZone(created_at, '${userTimezone}') BETWEEN ${startTime} and ${endTime}
        group by
            1
        order by
            orders_shipped desc
    `;

    return this.dynamicQueryService.fetchRaw(range, {
      raw_query: sql,
    });
  }

  getOrderShippedByCourier(range: AnalyticsRangeContext, timezone?: string) {
    const storeId = this.protectionService.getCurrentStoreSnapshot()?.id || 'default_store_id';
    const userTimezone = timezone || this.dynamicQueryService.getUserTimezone();
    const startTime = this.dynamicQueryService.toLocalTimezone(range.range.start, userTimezone);
    const endTime = this.dynamicQueryService.toLocalTimezone(range.range.end, userTimezone);

    const sql = `select
        TRIM(courier_company_name) AS courier,
        uniqExact(order_id) AS orders_shipped
        FROM ce_replica.\`ce_sales.sales_order_shipment\`
        WHERE
          store_id = '${storeId}'
          AND status NOT IN ['cancelled', 'pending']
          AND toTimeZone(created_at, '${userTimezone}') BETWEEN ${startTime} AND ${endTime}
        GROUP BY
          1
        ORDER BY
          orders_shipped desc
          `;

    return this.dynamicQueryService.fetchRaw(range, {
      raw_query: sql,
    });
  }
}
